import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useQuiz } from '../contexts/QuizContext';
import { RadarChart } from '../components/charts/RadarChart';
import {
  getStudents,
  getTeacherClasses,
  getStudentPerformance,
  getStudentQuizAttempts,
  TeacherStudent,
  TeacherClass,
  StudentPerformanceData
} from '../services/teacherService';

/**
 * TeacherDashboard Component
 * 
 * A comprehensive dashboard for teachers to monitor student performance and class statistics.
 * 
 * Features:
 * - Class selection: Teachers can view and select from classes they teach
 * - Student management: View and select students within a chosen class
 * - Performance visualization: Displays radar charts for subject and topic performance
 * - Quiz history: Shows detailed quiz results for selected students
 * - Learning recommendations: Provides personalized learning suggestions based on performance
 * - Class overview: Displays aggregate statistics for the entire class
 * 
 * The component uses mock data for demonstration but is structured to work with real API data.
 * It includes authorization checks to ensure only teachers can access the dashboard.
 */
const TeacherDashboard: React.FC = () => {
  const { currentUser, isTeacher } = useUser();
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
  const [studentPerformance, setStudentPerformance] = useState<StudentPerformanceData | null>(null);

  // State for real data
  const [teacherClasses, setTeacherClasses] = useState<TeacherClass[]>([]);
  const [classStudents, setClassStudents] = useState<TeacherStudent[]>([]);
  const [studentResults, setStudentResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load teacher data on component mount
  useEffect(() => {
    const loadTeacherData = async () => {
      if (!currentUser || !isTeacher()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load teacher's classes
        const classes = await getTeacherClasses(currentUser.userId);
        setTeacherClasses(classes);

        // Auto-select first class if available
        if (classes.length > 0) {
          setSelectedClass(classes[0].id);
        }
      } catch (err) {
        console.error('Error loading teacher data:', err);
        setError('Failed to load teacher data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadTeacherData();
  }, [currentUser, isTeacher]);

  // Load students when class is selected
  useEffect(() => {
    const loadClassStudents = async () => {
      if (!selectedClass) {
        setClassStudents([]);
        return;
      }

      try {
        setError(null);
        const students = await getStudents();
        setClassStudents(students);

        // Auto-select first student if available
        if (students.length > 0) {
          setSelectedStudent(students[0].userId);
        }
      } catch (err) {
        console.error('Error loading students:', err);
        setError('Failed to load students. Please try again.');
      }
    };

    loadClassStudents();
  }, [selectedClass]);

  // Load student performance and quiz results when student is selected
  useEffect(() => {
    const loadStudentData = async () => {
      if (!selectedStudent) {
        setStudentPerformance(null);
        setStudentResults([]);
        return;
      }

      try {
        setError(null);

        // Load performance data and quiz results in parallel
        const [performance, quizResults] = await Promise.all([
          getStudentPerformance(selectedStudent),
          getStudentQuizAttempts(selectedStudent)
        ]);

        setStudentPerformance(performance);
        setStudentResults(quizResults);
      } catch (err) {
        console.error('Error loading student data:', err);
        setError('Failed to load student performance data.');
      }
    };

    loadStudentData();
  }, [selectedStudent]);

  if (!isTeacher()) {
    return (
      <div className="container mx-auto py-12 px-4 text-center">
        <p className="text-xl">You don't have access to the teacher dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto py-12 px-4 text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-xl mt-4">Loading teacher dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-12 px-4 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800 text-xl">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-section-heading">Teacher Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="card-main mb-6">
            <h2 className="text-content-title mb-3">Your Classes</h2>
            <div className="space-y-2">
              {teacherClasses.map(cls => (
                <div
                  key={cls.id}
                  className={`p-3 rounded-md cursor-pointer ${selectedClass === cls.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  onClick={() => {
                    setSelectedClass(cls.id);
                    setSelectedStudent(null);
                  }}
                >
                  {cls.name}
                </div>
              ))}

              {teacherClasses.length === 0 && (
                <p className="text-gray-500 text-center py-4">No classes found.</p>
              )}
            </div>
          </div>

          {selectedClass && (
            <div className="card-main">
              <h2 className="text-content-title mb-3">Students</h2>
              <div className="space-y-2">
                {classStudents.map(student => (
                  <div
                    key={student.userId}
                    className={`p-3 rounded-md cursor-pointer ${selectedStudent === student.userId
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    onClick={() => setSelectedStudent(student.userId)}
                  >
                    {student.firstName} {student.lastName}
                  </div>
                ))}

                {classStudents.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No students in this class.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {selectedStudent && studentPerformance ? (
            <div>
              <div className="card-main mb-6">
                <h2 className="text-xl font-semibold">
                  {classStudents.find(s => s.userId === selectedStudent)?.firstName} {classStudents.find(s => s.userId === selectedStudent)?.lastName}'s Performance
                </h2>

                <div className="grid-responsive-2-gap mt-4">
                  <div className="h-[500px]">
                    {Object.keys(studentPerformance.subjectBreakdown).length > 0 ? (
                      <RadarChart
                        labels={Object.keys(studentPerformance.subjectBreakdown)}
                        data={Object.values(studentPerformance.subjectBreakdown).map((subject: any) => subject.averageScore)}
                        title="Subject Performance (%)"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        <p>No subject performance data available</p>
                      </div>
                    )}
                  </div>

                  <div className="h-[500px]">
                    <div className="bg-gray-50 p-4 rounded-lg h-full flex flex-col justify-center">
                      <h3 className="text-lg font-semibold mb-4 text-center">Overall Statistics</h3>
                      <div className="space-y-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{studentPerformance.quizAttempts}</p>
                          <p className="text-sm text-gray-600">Quiz Attempts</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">
                            {studentPerformance.totalQuestions > 0
                              ? Math.round((studentPerformance.correctAnswers / studentPerformance.totalQuestions) * 100)
                              : 0}%
                          </p>
                          <p className="text-sm text-gray-600">Overall Accuracy</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-purple-600">{studentPerformance.totalQuestions}</p>
                          <p className="text-sm text-gray-600">Total Questions Answered</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-main">
                <h2 className="text-card-title">Quiz History</h2>

                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quiz Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {studentResults.map((attempt: any) => {
                      const score = attempt.total_questions > 0
                        ? Math.round((attempt.correct_answers / attempt.total_questions) * 100)
                        : 0;
                      return (
                        <tr key={attempt.attempt_id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            Quiz #{attempt.attempt_id.substring(0, 8)}
                            {attempt.started_from_content_point_id && ' (Content Point)'}
                            {attempt.started_from_substrand_id && ' (Substrand)'}
                            {attempt.started_from_strand_id && ' (Strand)'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {new Date(attempt.started_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${score >= 80 ? 'bg-green-100 text-green-800' :
                              score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                              {score}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}

                    {studentResults.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                          No quiz results found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="card-main mt-6">
                <h2 className="text-card-title">Learning Recommendations</h2>

                <div className="space-y-4">
                  {studentPerformance && studentPerformance.averageScore < 70 ? (
                    <div className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                      <h3 className="font-medium text-lg text-orange-800">Areas for Improvement</h3>
                      <p className="text-orange-700 mt-1">
                        Student's current average score is {Math.round(studentPerformance.averageScore)}%.
                        Consider reviewing fundamental concepts and providing additional practice opportunities.
                      </p>
                      <div className="mt-3 flex space-x-2">
                        <span className="badge-pill bg-orange-100 text-orange-800">
                          Additional Practice
                        </span>
                        <span className="badge-pill bg-orange-100 text-orange-800">
                          Review Sessions
                        </span>
                      </div>
                    </div>
                  ) : studentPerformance && studentPerformance.averageScore >= 70 ? (
                    <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                      <h3 className="font-medium text-lg text-green-800">Good Progress</h3>
                      <p className="text-green-700 mt-1">
                        Student is performing well with {Math.round(studentPerformance.averageScore)}% average score.
                        Consider introducing more challenging questions to maintain engagement.
                      </p>
                      <div className="mt-3 flex space-x-2">
                        <span className="badge-pill bg-green-100 text-green-800">
                          Advanced Topics
                        </span>
                        <span className="badge-pill bg-green-100 text-green-800">
                          Challenge Questions
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      No performance data available yet. Student needs to complete more quizzes.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="card-main p-8 text-center">
              <h2 className="text-xl font-semibold text-gray-500">
                {selectedClass ? 'Select a student to view their performance' : 'Select a class to begin'}
              </h2>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 card-main">
        <h2 className="text-card-title">Class Overview</h2>

        {selectedClass ? (
          <div className="grid-responsive-3 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-800">Average Class Score</h3>
              <p className="text-3xl font-bold mt-2">
                {studentPerformance ? `${Math.round(studentPerformance.averageScore)}%` : 'N/A'}
              </p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-medium text-green-800">Total Quizzes Taken</h3>
              <p className="text-3xl font-bold mt-2">
                {studentResults.length || 0}
              </p>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-medium text-purple-800">Active Students</h3>
              <p className="text-3xl font-bold mt-2">
                {classStudents.length}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            Select a class to view overview statistics.
          </p>
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard;